import { useAiToolkitContext } from '@app/(darkHeader)/ai/_shared/_context/AiToolkitContext';
import { AI_TOOLKIT_CATEGORIES, AiToolkitCategory } from '@app/(darkHeader)/ai/_shared/_context/types';
import { usePathname, useRouter } from 'next/navigation';
import { type JSX, type ReactNode, useCallback } from 'react';

import { ConfirmationModal } from '@videoblocks/design-system';

import { pluralize } from '../../../../../../utils/utils';
import { getCreditCost } from '../../_context/getCreditCost';

const getModalContent = (category: AiToolkitCategory, calculatedCost: number, credits: number): ReactNode => {
    switch (category) {
        case AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION:
            return (
                <div className="flex flex-col gap-4">
                    <span>{`You need ${calculatedCost} ${pluralize(calculatedCost, 'credit')} to generate this video.`}</span>
                    <span>{`Your balance is ${credits} ${pluralize(credits, 'credit')}.`}</span>
                </div>
            );
        case AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION:
            return (
                <div className="flex flex-col gap-4">
                    <span>{`You need ${calculatedCost} ${pluralize(calculatedCost, 'credit')} to generate this image.`}</span>
                    <span>{`Your balance is ${credits} ${pluralize(credits, 'credit')}.`}</span>
                </div>
            );
        case AI_TOOLKIT_CATEGORIES.VOICEOVER:
            return '';
    }
};

export const InsufficientCreditsModal = (): JSX.Element => {
    const { confirmationModal, dispatchConfirmationModal, credits, formStates, subscriptionId } = useAiToolkitContext();
    const pathname = usePathname();
    const router = useRouter();

    const { type, category = AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION } = confirmationModal ?? {};
    const isOpen = type === 'insufficientCredits' && confirmationModal?.isOpen === true;
    const calculatedCost = getCreditCost(category, formStates);

    const onClose = useCallback((): void => {
        dispatchConfirmationModal({ type: 'CLOSE_MODAL' });
    }, [dispatchConfirmationModal]);

    const handleGetCredits = useCallback((): void => {
        dispatchConfirmationModal({ type: 'CLOSE_MODAL' });
        const redirect = encodeURIComponent(pathname);
        router.push(`/video/upgrade-my-plan/${subscriptionId}?redirectAfterRegister=${redirect}`);
    }, [subscriptionId, pathname, router, dispatchConfirmationModal]);

    return (
        <ConfirmationModal
            isOpen={isOpen}
            title="Insufficient credits"
            content={getModalContent(category, calculatedCost, credits)}
            confirmLabel="Get Credits"
            cancelLabel="Cancel"
            onConfirm={handleGetCredits}
            onCancel={onClose}
        />
    );
};
